// src/components/games/GuestSignupPrompt.jsx
// 🟢 [访客模式] 结算后的「5 秒极速注册」引导。
//
// 关于「5 秒」：这是成本承诺，不是倒计时。
// 只要 3 个字段（姓名/邮箱/密码），确实 5 秒能填完；
// 而且任何时候都不会删除访客的成绩 —— 「以后再说」只是把弹窗收成一条细横幅，
// 成绩还在 sessionStorage 里，F5 之后还能领。给小孩子设「不注册就没收成绩」的
// 倒计时既做不到（还要填表单）又像陷阱。
import React, { useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import AppIcon from '../common/AppIcon';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { loadGuestResult, clearGuestResult, toScorePayload } from '../../utils/guestSession';

// 把各种失败翻译成小孩看得懂的话。
// ⚠️ src/services/api.js 的响应拦截器把 HTTP status 丢掉了，只留 message，
//    所以这里只能按 message / Firebase err.code 匹配，不能按状态码分支。
//    正因如此，「待审批」是从 register 的响应体判断的，而不是从 catch 里判断。
const mapAuthError = (err, mode) => {
  const msg = (err?.message || '').toLowerCase();
  const code = err?.code || '';

  if (msg.includes('already in use') || msg.includes('already exists')) {
    return 'That email already has an account.';
  }
  if (msg.includes('six characters') || msg.includes('at least 6') || code === 'auth/weak-password') {
    return 'Password must be at least 6 characters.';
  }
  if (msg.includes('invalid email') || code === 'auth/invalid-email') {
    return 'That email address looks wrong.';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Wrong email or password.';
  }
  if (msg.includes('pending approval') || msg.includes('not approved')) {
    return 'Your account is waiting for admin approval.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many tries. Please wait a minute and try again.';
  }
  return err?.message || (mode === 'login' ? 'Could not log you in.' : 'Could not create your account.');
};

// 视觉语言参考 LessonPage 的 CompletionModal（复制而非 import —— 那个是课程专用的）
const Overlay = ({ children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl border-4 border-white/50 bg-white p-7 text-center shadow-2xl"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  </div>
);

const GuestSignupPrompt = ({ onPlayAgain, bannerOnly = false }) => {
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [mode, setMode] = useState('signup');   // 'signup' | 'login'
  const [phase, setPhase] = useState('idle');   // idle | busy | pending | claimFailed | done
  const [error, setError] = useState('');
  const [earned, setEarned] = useState(null);
  // bannerOnly：不是刚结算（例如 F5 后回到菜单页），直接以细横幅形态出现，
  // 这样「成绩存在这台设备上、随时可以领」才是真的
  const [dismissed, setDismissed] = useState(bannerOnly);

  // 挂载时读一次即可：本局成绩在 endGame 里已经写好了
  const result = useMemo(() => loadGuestResult(), []);
  const busyRef = useRef(false);   // 双击 / StrictMode 双调用保护

  const displayScore = result?.displayScore ?? result?.score ?? 0;
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // 领取这一局。只可能成功执行一次。
  // 返回 true = 已入账（或本来就没有可领的分数），false = 账号建好了但补分失败。
  const claimStoredResult = async () => {
    const stored = loadGuestResult();
    if (!stored || stored.score <= 0) return true;

    // ⚠️ 先清后发：网络重试 / 双击 / StrictMode 双调用都无法重放这一局。
    clearGuestResult();
    try {
      const saveRes = await apiService.saveGameScore(toScorePayload(stored));
      // 显示后端真实入账的分数，而不是客户端原始分
      // (routes/games.js 会 clamp 到 500，还可能加每日任务奖励)
      setEarned(saveRes?.pointsEarned ?? null);
      const totalGames = saveRes?.totalGamesPlayed || 0;
      // 徽章失败不影响补分成功：徽章是后端重算 + 幂等的，下一局会补上
      try {
        if (totalGames > 0) await apiService.checkBadges('game_played', totalGames);
      } catch (badgeErr) {
        console.error('Badge check failed:', badgeErr);
      }
      return true;
    } catch (e) {
      // 账号已经建好并登录了，只有这一局没入账 —— 不要把整个流程判为失败
      console.error('Guest score claim failed:', e);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true;
    setError('');
    setPhase('busy');

    try {
      if (mode === 'signup') {
        // 与 RegisterPage.jsx 的客户端校验保持一致，省一次往返
        if (form.password.length < 6) {
          throw new Error('Password must be at least 6 characters.');
        }

        const reg = await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          role: 'student'
        });

        // 🔴 关键分支：pending 状态下【绝对不要】尝试登录。
        // POST /api/auth/login 会 403 (routes/auth.js)，AuthContext.login 会
        // signOut 并抛错，补分永远不会发生，用户还会落到 PendingApprovalPage。
        const approved = reg?.status === 'approved' || reg?.user?.status === 'approved';
        if (!approved) {
          setPhase('pending');   // 成绩留在 sessionStorage 里，不清除
          return;
        }
      }

      // 复用现成的 AuthContext.login (Firebase 登录 + POST /auth/login + setUser)
      await login(form.email.trim(), form.password);

      const claimed = await claimStoredResult();

      if (!claimed) {
        // 账号可用，只是这一局没入账 —— 该分支自己有按钮，不自动跳转
        setPhase('claimFailed');
        return;
      }

      setPhase('done');
      // 先让用户看清 "+N points added!" 再进 dashboard
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      setError(mapAuthError(err, mode));
      setPhase('idle');
    } finally {
      busyRef.current = false;
    }
  };

  // ---------- 已收起：细横幅（成绩仍可领取） ----------
  if (dismissed && phase !== 'pending') {
    return (
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4"
      >
        <button
          onClick={() => setDismissed(false)}
          className="mx-auto flex w-full max-w-md items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-4 font-bold text-white shadow-2xl transition-transform active:scale-95"
        >
          <span className="text-xl">🎁</span>
          Claim your {displayScore} points
          <AppIcon name="arrow-right" className="ml-1" />
        </button>
      </motion.div>
    );
  }

  // ---------- 待审批 ----------
  if (phase === 'pending') {
    return (
      <Overlay>
        <div className="text-6xl mb-4">📬</div>
        <h2 className="text-2xl font-black text-slate-800 mb-2">Account created!</h2>
        <p className="text-slate-600 font-medium mb-6 px-2">
          A teacher needs to approve it first. Your score of{' '}
          <strong className="text-sky-600">{displayScore}</strong> is saved on this device — it will be
          added as soon as you log in.
        </p>
        <Link
          to="/login"
          className="block w-full rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 py-4 text-lg font-bold text-white shadow-lg transition-transform active:scale-95"
        >
          Go to Log In
        </Link>
        <button onClick={onPlayAgain} className="mt-3 py-2 text-sm font-bold text-slate-400 hover:text-slate-600">
          Keep playing
        </button>
      </Overlay>
    );
  }

  // ---------- 账号建好了但补分失败 ----------
  if (phase === 'claimFailed') {
    return (
      <Overlay>
        <div className="text-6xl mb-4">✅</div>
        <h2 className="text-2xl font-black text-slate-800 mb-2">You are all set!</h2>
        <p className="text-slate-600 font-medium mb-6 px-2">
          We could not add this round&apos;s points, but your account is ready. Play again from your
          dashboard and your points will count.
        </p>
        <button
          onClick={() => navigate('/dashboard', { replace: true })}
          className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 py-4 text-lg font-bold text-white shadow-lg transition-transform active:scale-95"
        >
          Go to My Dashboard
        </button>
      </Overlay>
    );
  }

  // ---------- 领取成功（导航前的一瞬间） ----------
  if (phase === 'done') {
    return (
      <Overlay>
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-black text-slate-800 mb-2">
          {earned !== null ? `+${earned} points added!` : 'Progress saved!'}
        </h2>
        <p className="text-slate-600 font-medium">Taking you to your dashboard…</p>
      </Overlay>
    );
  }

  // ---------- 主界面：极速注册 ----------
  const busy = phase === 'busy';
  const isLogin = mode === 'login';

  return (
    <Overlay>
      <div className="text-5xl mb-3">🏆</div>

      <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Your Score</div>
      <div className="mb-3 text-5xl font-black text-sky-600">{displayScore}</div>

      <h2 className="mb-2 text-xl font-black text-slate-800">
        {isLogin ? 'Log in to save your score' : 'Create a free account to keep it'}
      </h2>
      <p className="mb-5 px-2 text-sm font-medium text-slate-500">
        {isLogin
          ? 'Log in and this round gets added to your points.'
          : 'Sign up free and turn this score into points, badges and a spot on the leaderboard.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-3 text-left">
        {!isLogin && (
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            required
            autoFocus
            autoComplete="name"
            placeholder="Your name"
            disabled={busy}
            className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 font-medium text-slate-800 outline-none transition-colors focus:border-sky-400 disabled:bg-slate-100"
          />
        )}
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          required
          autoFocus={isLogin}
          autoComplete="email"
          placeholder="Email"
          disabled={busy}
          className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 font-medium text-slate-800 outline-none transition-colors focus:border-sky-400 disabled:bg-slate-100"
        />
        <input
          name="password"
          type="password"
          value={form.password}
          onChange={onChange}
          required
          minLength={6}
          autoComplete={isLogin ? 'current-password' : 'new-password'}
          placeholder="Password"
          disabled={busy}
          className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 font-medium text-slate-800 outline-none transition-colors focus:border-sky-400 disabled:bg-slate-100"
        />

        {error && (
          <div className="rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
            {error}
            {/* 邮箱已被占用：一键切到登录模式，成绩照样能领 */}
            {error.includes('already has an account') && (
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                className="mt-2 block font-bold text-sky-600 underline"
              >
                Log in instead →
              </button>
            )}
            {isLogin && error.includes('Wrong email or password') && (
              <Link to="/forgot-password" className="mt-2 block font-bold text-sky-600 underline">
                Forgot your password?
              </Link>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 py-4 text-lg font-bold text-white shadow-lg shadow-sky-200 transition-transform active:scale-95 disabled:opacity-60"
        >
          {busy ? (
            <><AppIcon name="spinner" className="mr-2" /> Saving…</>
          ) : isLogin ? 'Log in & save my score' : 'Save my progress'}
        </button>
      </form>

      {!isLogin && (
        <div className="mt-3 text-xs font-bold text-slate-400">
          ⏱ About 5 seconds — 3 fields, that&apos;s it
        </div>
      )}

      <p className="mt-3 text-xs font-medium text-slate-400">
        Your score is saved on this device — claim it whenever you&apos;re ready.
      </p>

      <div className="mt-4 flex items-center justify-center gap-4 text-sm">
        <button onClick={() => setDismissed(true)} className="font-bold text-slate-400 hover:text-slate-600">
          Maybe later
        </button>
        <span className="text-slate-300">·</span>
        <button
          onClick={() => { setMode(isLogin ? 'signup' : 'login'); setError(''); }}
          className="font-bold text-sky-500 hover:text-sky-700"
        >
          {isLogin ? 'Create an account' : 'I already have an account'}
        </button>
      </div>
    </Overlay>
  );
};

export default GuestSignupPrompt;
